import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { api } from "@/api";
import { STYLED_ORDER_CATEGORIES, STYLE_ORDER, HIDDEN_STYLES_PER_CATEGORY, CONTACT } from "@/constants";
import { toast } from "sonner";
import { Upload, X, Info, MessageCircle, Mail } from "lucide-react";

export default function OrderDialog({ open, category, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", description: "", style: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", phone: "", description: "", style: "" });
      setFile(null);
      setPreview(null);
      setSubmitting(false);
    }
  }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  };

  const showStyle = category && STYLED_ORDER_CATEGORIES.has(category);
  const visibleStyles = STYLE_ORDER.filter(
    (s) => !(HIDDEN_STYLES_PER_CATEGORY[category]?.has(s))
  );
  const whatsappHref = `https://wa.me/${CONTACT.phone.replace(/\D/g, "")}?text=${encodeURIComponent("Hi, I'd like a quote for a 3D print order.")}`;
  const mailHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Quote request — All Possible 3D Solutions")}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return;
    setSubmitting(true);
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          file_id = data.file_id;
        } catch {
          toast.error(t.order.upload_error);
        }
      }
      await api.post("/orders", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        description: form.description,
        category,
        file_id,
        style: showStyle ? form.style || null : null,
      });
      toast.success(t.order.success);
      onClose();
    } catch (err) {
      toast.error(t.order.error);
    } finally {
      setSubmitting(false);
    }
  };

  const catMeta = category ? t.categories[category] : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="order-modal"
        className="bg-[#0d0d0d] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight flex items-center gap-2 flex-wrap">
            {t.order.title}
            {catMeta && (
              <span className="font-mono-accent text-[11px] uppercase bg-brand-blue/15 border border-brand-blue/40 text-brand-blue-light px-2 py-1 tracking-widest">
                {catMeta.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">{t.order.subtitle}</DialogDescription>
        </DialogHeader>

        <div
          data-testid="order-pricing-notice"
          className="mt-2 flex gap-3 bg-brand-blue/10 border border-brand-blue/30 p-3.5 text-[12.5px] text-zinc-200 leading-relaxed"
        >
          <Info size={16} className="flex-shrink-0 mt-0.5 text-brand-blue-light" />
          <div>
            {t.order.pricing_notice}
            <div className="mt-2 flex flex-wrap gap-2">
              <a
                data-testid="order-quote-whatsapp"
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 font-mono-accent uppercase text-[10px] tracking-widest"
              >
                <MessageCircle size={12} /> {t.order.whatsapp}
              </a>
              <a
                data-testid="order-quote-email"
                href={mailHref}
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/15 text-white px-3 py-1.5 font-mono-accent uppercase text-[10px] tracking-widest"
              >
                <Mail size={12} /> {t.order.contact_email}
              </a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
                {t.order.name}
              </Label>
              <Input
                required
                data-testid="order-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
              />
            </div>
            <div>
              <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
                {t.order.email}
              </Label>
              <Input
                required
                type="email"
                data-testid="order-form-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
              {t.order.phone}
            </Label>
            <Input
              data-testid="order-form-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
            />
          </div>

          {showStyle && (
            <div>
              <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
                {t.styles.label}
              </Label>
              <Select
                value={form.style}
                onValueChange={(v) => setForm({ ...form, style: v })}
              >
                <SelectTrigger
                  data-testid="order-form-style"
                  className="mt-1 rounded-none bg-black/60 border-white/10 focus:ring-brand-blue"
                >
                  <SelectValue placeholder={t.styles.placeholder} />
                </SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/10">
                  {visibleStyles.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`order-form-style-${s}`}>
                      {t.styles[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
              {t.order.description}
            </Label>
            <Textarea
              required
              rows={4}
              data-testid="order-form-description"
              placeholder={t.order.description_ph}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
            />
          </div>

          <div>
            <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
              {t.order.image}
            </Label>
            {!preview ? (
              <label
                htmlFor="order-file"
                className="mt-1 flex flex-col items-center justify-center border border-dashed border-white/20 hover:border-brand-blue/50 bg-black/40 cursor-pointer py-6 transition-colors"
              >
                <Upload size={18} className="text-brand-blue-light" />
                <div className="mt-2 text-sm text-zinc-300">{t.order.image}</div>
                <div className="text-[11px] font-mono-accent text-zinc-500 mt-1">
                  {t.order.image_hint}
                </div>
                <input
                  id="order-file"
                  data-testid="order-form-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                />
              </label>
            ) : (
              <div className="mt-1 relative border border-white/10">
                <img src={preview} alt="preview" className="w-full h-48 object-contain bg-black" />
                <button
                  type="button"
                  onClick={clearFile}
                  className="absolute top-2 right-2 bg-black/80 border border-white/10 p-1.5 hover:bg-black"
                  data-testid="order-form-clear-file"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="order-form-submit"
            className="w-full bg-brand-blue hover:bg-brand-blue-light text-white font-mono-accent uppercase tracking-widest text-xs py-6 rounded-none border border-brand-blue-light/50"
          >
            {submitting ? t.order.submitting : t.order.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
